// ============================================================================
// UnmatchedFeatures Component (View)
// ============================================================================

import { Box, Typography, Button, Alert, IconButton, CircularProgress } from "@mui/material";
import {
	Visibility,
	VisibilityOff,
	Delete as DeleteIcon,
	HighlightAlt,
	CallSplit,
	UploadFile,
	InsertDriveFileOutlined,
} from "@mui/icons-material";
import { IssueMap } from "../../components/IssueMap";
import { UNMATCHED_POINT_COLOR, NEARBY_JOB_COLOR } from "../../shared/constants";
import type { UnmatchedFeaturesProps } from "./UnmatchedFeatures.types";
import { useUnmatchedFeaturesViewModel } from "./UnmatchedFeatures.vm";
import "./UnmatchedFeatures.css";
import { PortalTooltip } from "../../../../../../helpers";

/** Accepted file types for boundary upload - matches GeoDataImportForm */
const ACCEPTED_FILE_TYPES = ".zip,.json,.geojson,.kml,.kmz,.shp,.shx,.dbf,.prj";

/**
 * Component for displaying and resolving unmatched feature issues
 */
export function UnmatchedFeatures(props: UnmatchedFeaturesProps) {
	const vm = useUnmatchedFeaturesViewModel(props);

	// No issues state
	if (!vm.hasIssues) {
		return (
			<Box data-testid="no-issues-message" className="unmatched-features no-issues">
				<Alert severity="success">
					<Typography>No unmatched points/zones detected. You can proceed to the next step.</Typography>
				</Alert>
			</Box>
		);
	}

	// No current issue (shouldn't happen but handle gracefully)
	if (!vm.currentIssue) {
		return null;
	}

	return (
		<Box className="unmatched-features">
			{/* Issue Description */}
			<Box className="issue-description">
				<Typography variant="body2" color="text.secondary">
					This job has points or zones but no boundary.
				</Typography>
			</Box>

			{/* Side-by-side layout: Content left, Map right */}
			<Box className="side-by-side-layout">
				{/* Left side - Content */}
				<Box className="left-side">
					{/* Unmatched Features Card - unified info and controls */}
					<Box className="unmatched-card">
						{/* Header with visibility toggle */}
						<Box className="card-header">
							{/* Color Swatch */}
							<Box
								className={`color-swatch ${vm.hideUnmatchedFeatures ? "hidden" : ""}`}
								sx={{
									bgcolor: UNMATCHED_POINT_COLOR,
									borderColor: vm.hideUnmatchedFeatures ? undefined : UNMATCHED_POINT_COLOR,
								}}
							/>

							{/* Visibility Toggle */}
							<PortalTooltip title={vm.hideUnmatchedFeatures ? "Show on map" : "Hide on map"}>
								<IconButton size="small" onClick={vm.toggleUnmatchedFeaturesVisibility} className="visibility-toggle">
									{vm.hideUnmatchedFeatures ? (
										<VisibilityOff fontSize="small" color="disabled" />
									) : (
										<Visibility fontSize="small" color="primary" />
									)}
								</IconButton>
							</PortalTooltip>

							{/* Label */}
							<Typography variant="subtitle2" sx={{ flex: 1, opacity: vm.hideUnmatchedFeatures ? 0.6 : 1 }}>
								Unmatched Points/Zones ({vm.unmatchedFeatureCount})
							</Typography>

							{/* Selection Tools */}
							<Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
								<PortalTooltip
									title={
										vm.selectedFeatureIds.size > 0
											? "Clear selection"
											: vm.lassoMode
												? "Click on map to add points, click first point to close"
												: "Select features on map"
									}
								>
									<IconButton
										size="small"
										onClick={vm.handleToggleLasso}
										sx={{
											color: vm.selectedFeatureIds.size > 0 ? "error.main" : vm.lassoMode ? "#2196f3" : undefined,
										}}
									>
										<HighlightAlt fontSize="small" />
									</IconButton>
								</PortalTooltip>
								<PortalTooltip title="Split selected features into a new group">
									<span>
										<IconButton
											size="small"
											onClick={vm.handleSplitFeatures}
											disabled={!vm.canSplit}
											color={vm.canSplit ? "primary" : "default"}
										>
											<CallSplit fontSize="small" />
										</IconButton>
									</span>
								</PortalTooltip>
							</Box>
						</Box>

						{/* Selection Info */}
						{vm.selectedFeatureIds.size > 0 && (
							<Box sx={{ px: 2, pb: 1 }}>
								<Typography variant="body2" color="error.main">
									{vm.selectedFeatureIds.size} feature{vm.selectedFeatureIds.size !== 1 ? "s" : ""} selected
								</Typography>
							</Box>
						)}

						{/* Content */}
						<Box className="card-content">
							{/* Source File */}
							<Box className="source-file-box">
								<Box className="source-file-name">
									<InsertDriveFileOutlined className="source-file-icon" />
									<Typography variant="body2">{vm.sourceFileName}</Typography>
								</Box>
								{/* Hidden file input for boundary upload */}
								<input
									type="file"
									ref={vm.fileInputRef}
									style={{ display: "none" }}
									accept={ACCEPTED_FILE_TYPES}
									multiple
									onChange={vm.handleFileChange}
								/>
								<Box sx={{ display: "flex", alignItems: "center" }}>
									{/* Upload boundary button */}
									{vm.canUpload && (
										<PortalTooltip title="Upload boundary file">
											<span>
												<IconButton color="primary" onClick={vm.handleUploadClick} disabled={vm.isUploading}>
													{vm.isUploading ? <CircularProgress size={18} /> : <UploadFile fontSize="small" />}
												</IconButton>
											</span>
										</PortalTooltip>
									)}
									<PortalTooltip title="Delete this job">
										<IconButton color="error" onClick={vm.handleDeleteJob}>
											<DeleteIcon fontSize="small" />
										</IconButton>
									</PortalTooltip>
								</Box>
							</Box>
						</Box>
					</Box>

					{/* Nearby Jobs - Map Legend */}
					{vm.currentIssue.nearbyJobs.length > 0 && (
						<Box className="nearby-jobs-box">
							{/* Nearby Jobs Header */}
							<Box className="nearby-jobs-header">
								<Typography variant="subtitle2">Nearby Jobs ({vm.currentIssue.nearbyJobs.length})</Typography>
								<Box className="controls">
									<Button
										size="small"
										variant="text"
										onClick={vm.showAllNearbyJobs}
										disabled={vm.hiddenNearbyJobIds.size === 0}
									>
										Show All
									</Button>
									<Button
										size="small"
										variant="text"
										onClick={vm.hideAllNearbyJobs}
										disabled={vm.hiddenNearbyJobIds.size === vm.currentIssue.nearbyJobs.length}
									>
										Hide All
									</Button>
								</Box>
							</Box>

							{/* Nearby Jobs List */}
							<Box className="nearby-job-list">
								{vm.currentIssue.nearbyJobs.map((nearby, index) => {
									const isVisible = !vm.hiddenNearbyJobIds.has(nearby.job.id);

									return (
										<Box
											key={nearby.job.id}
											className={`nearby-job-item ${isVisible ? "" : "hidden"}`}
											sx={{
												borderBottom: index < vm.currentIssue!.nearbyJobs.length - 1 ? 1 : 0,
												borderColor: "divider",
											}}
										>
											{/* Color Swatch */}
											<Box
												className={`color-swatch ${isVisible ? "" : "hidden"}`}
												sx={{
													bgcolor: NEARBY_JOB_COLOR,
													borderColor: isVisible ? NEARBY_JOB_COLOR : undefined,
												}}
											/>

											{/* Visibility Toggle */}
											<PortalTooltip title={isVisible ? "Hide on map" : "Show on map"}>
												<IconButton
													size="small"
													onClick={() => vm.toggleNearbyJobVisibility(nearby.job.id)}
													className="visibility-toggle"
												>
													{isVisible ? (
														<Visibility fontSize="small" color="primary" />
													) : (
														<VisibilityOff fontSize="small" color="disabled" />
													)}
												</IconButton>
											</PortalTooltip>

											{/* Job Info */}
											<Box className="job-info">
												<Typography variant="body2" fontWeight={500} noWrap>
													{nearby.job.fieldName || nearby.job.importFieldName || "Unknown Field"}
												</Typography>
												<Typography variant="caption" color="text.secondary" noWrap>
													{nearby.distanceMiles.toFixed(2)} miles away
												</Typography>
											</Box>

											{/* Assign Button */}
											<PortalTooltip title="Add the unmatched points/zones to this job">
												<Button
													size="small"
													variant="outlined"
													data-testid={`assign-to-job-${nearby.job.id}`}
													onClick={() => vm.handleAssignToJob(nearby.job.id)}
													className="assign-button"
												>
													Add to this Job
												</Button>
											</PortalTooltip>
										</Box>
									);
								})}
							</Box>
						</Box>
					)}
				</Box>

				{/* Right side - Map */}
				<Box className="right-side">
					<IssueMap
						jobs={[vm.currentIssue.job]}
						visualizationMode="unmatched"
						nearbyJobs={vm.currentIssue.nearbyJobs}
						unmatchedFeatures={vm.currentIssue.unmatchedFeatures}
						hideUnmatchedFeatures={vm.hideUnmatchedFeatures}
						hiddenNearbyJobIds={Array.from(vm.hiddenNearbyJobIds)}
						height="100%"
						lassoMode={vm.lassoMode}
						selectedFeatureIds={vm.selectedFeatureIds}
						onLassoComplete={vm.handleLassoComplete}
					/>
				</Box>
			</Box>
		</Box>
	);
}
